import React, { useState, useEffect } from 'react';
import { CronExpression } from '../../lib/parsers/cronParser';

export const CronParser: React.FC = () => {
  const [input, setInput] = useState('');
  const [cron, setCron] = useState<CronExpression | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (input.trim()) {
      try {
        const newCron = new CronExpression(input);
        setCron(newCron);
        setError('');
      } catch (err) {
        setCron(null);
        setError(err instanceof Error ? err.message : 'Invalid cron expression');
      }
    } else {
      setCron(null);
      setError('');
    }
  }, [input]);

  // const validateInput = (value: string): string => {
  //   if (!value.trim()) return '';

  //   const parts = value.trim().split(' ');
  //   if (parts.length !== 5) {
  //     return 'Cron expression must have 5 fields separated by spaces';
  //   }

  //   // Additional validation can be added here
  //   return '';
  // };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePresetClick = (preset: string) => {
    setInput(preset);
  };

  return (
    <div className="cron-parser" role="main">
      <h1>Cron Parser</h1>
      <p>Paste your cron expression to see it in human-readable format and get the next execution times.</p>

      <div className="input-section">
        <label htmlFor="cron-input">Cron Expression:</label>
        <input
          id="cron-input"
          type="text"
          placeholder="0 9 * * *"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-describedby="cron-help"
          data-testid="cron-input"
        />
        <small id="cron-help">
          Format: minute hour day-of-month month day-of-week
        </small>
      </div>

      <div className="presets-section">
        <h3>Quick Presets</h3>
        <div className="presets">
          <button onClick={() => handlePresetClick('0 9 * * *')}>
            Daily at 9:00 AM
          </button>
          <button onClick={() => handlePresetClick('0 * * * *')}>
            Every hour
          </button>
          <button onClick={() => handlePresetClick('0 9 * * 1')}>
            Weekly on Monday
          </button>
          <button onClick={() => handlePresetClick('*/15 9-17 * * 1-5')}>
            Weekdays every 15 min
          </button>
          <button onClick={() => handlePresetClick('*/15 9-17 * * 1-6')}>
            Mon-Sat every 15 min
          </button>
          <button onClick={() => handlePresetClick('*/30 * * * *')}>
            Every 30 minutes
          </button>
          <button onClick={() => handlePresetClick('*/10 8-18 * * 1-5')}>
            Weekdays every 10 min (8-18)
          </button>
          <button onClick={() => handlePresetClick('*/5 * * * *')}>
            Every 5 minutes
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert" data-testid="error-message">
          {error}
        </div>
      )}

      {cron && (
        <div className="results">
          <div className="result-section">
            <h3>Human Readable Format</h3>
            <p className="human-readable" data-testid="human-readable">
              {cron.humanReadable}
            </p>
          </div>

          {cron.isValid && (
            <div className="result-section">
              <h3>Next Execution Times</h3>
              <ul className="next-executions" data-testid="next-executions">
                {cron.nextExecutionTimes.map((time, index) => (
                  <li key={index}>{time}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="result-section">
            <h3>Components</h3>
            <table className="components-table">
              <tbody>
                <tr>
                  <td>Minute</td>
                  <td>{cron.parsedComponents.minute}</td>
                </tr>
                <tr>
                  <td>Hour</td>
                  <td>{cron.parsedComponents.hour}</td>
                </tr>
                <tr>
                  <td>Day of Month</td>
                  <td>{cron.parsedComponents.dayOfMonth}</td>
                </tr>
                <tr>
                  <td>Month</td>
                  <td>{cron.parsedComponents.month}</td>
                </tr>
                <tr>
                  <td>Day of Week</td>
                  <td>{cron.parsedComponents.dayOfWeek}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="actions">
            <button
              onClick={() => handleCopy(cron.humanReadable)}
              disabled={!cron.isValid}
              data-testid="copy-button"
            >
              Copy Human Readable
            </button>
            <button
              onClick={() => handleCopy(cron.nextExecutionTimes.join('\n'))}
              disabled={!cron.isValid}
            >
              Copy Next Times
            </button>
          </div>
        </div>
      )}
    </div>
  );
};